import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Label,
} from "reactstrap";
import Breadcrumbs from "../../coreComponents/breadcrumbs";
import { format, endOfMonth } from "date-fns";
import ApiService from "../../services/apiService";
import { toast } from "react-toastify";
import LeftIcon from "../svg/LeftButton";
import RightIcon from "../svg/RightIcon";
import ExportExcel from "../reports/ExportExcel";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const HeadQuarterWaterReport = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [fromMonth, setFromMonth] = useState<number>(currentMonth);
  const [toMonth, setToMonth] = useState<number>(currentMonth);
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const [pumpingStationData, setPumpingStationData] = useState<any[]>([]);
  const [selectedStationName, setSelectedStationName] = useState<any>(null);
  const [selectedStationId, setSelectedStationId] = useState<any>(null);

  const [stationDropdownOpen, setStationDropdownOpen] = useState(false);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [fromMonthDropdownOpen, setFromMonthDropdownOpen] = useState(false);
  const [toMonthDropdownOpen, setToMonthDropdownOpen] = useState(false);

  const [data, setData] = useState<any[]>([]);
  const [headQuarters, setHeadQuarters] = useState<string[]>([]);
  const [dateList, setDateList] = useState<string[]>([]);

  const [pageNumber, setPageNumber] = useState<number>(1);
  const pageSize = 10;
  const [totalCount, setTotalCount] = useState<number>(0);
  const totalPages = Math.ceil(totalCount / pageSize);

  useEffect(() => {
    const getPumpingStation = async () => {
      try {
        const response = await ApiService.axiosApi(`/pumpingstation`, {
          method: "GET",
          params: { pageNumber: 0, pageSize: 0 },
        });
        if (response.success && response.data.data.length > 0) {
          setPumpingStationData(response.data.data);
          setSelectedStationName(response.data.data[0].name);
          setSelectedStationId(response.data.data[0].id);
        }
      } catch (e) {
        console.log(e);
      }
    };
    getPumpingStation();
  }, []);

  const validateGenerate = () => {
    if (!selectedStationName) {
      toast.warning("Please select Pumping Station");
      return false;
    }
    if (fromMonth > toMonth) {
      toast.warning("From Month cannot be greater than To Month");
      return false;
    }
    return true;
  };

  const transformData = (raw: any[]) => {
    const hqSet = new Set<string>();
    const dateSet = new Set<string>();

    raw.forEach((r) => {
      hqSet.add(r.headQuaterName);
      dateSet.add(r.dateTime);
    });

    const hqs = Array.from(hqSet).sort();
    const dates = Array.from(dateSet).sort((a, b) => {
      const [ay, am] = a.split("-");
      const [by, bm] = b.split("-");
      const aIdx = MONTH_NAMES.indexOf(am);
      const bIdx = MONTH_NAMES.indexOf(bm);
      return (
        new Date(+ay, aIdx, 1).getTime() - new Date(+by, bIdx, 1).getTime()
      );
    });

    setHeadQuarters(hqs);
    setDateList(dates);
    setData(raw);
    setTotalCount(dates.length);
  };

  const fetchData = async (pNum = pageNumber) => {
    const from = new Date(selectedYear, fromMonth, 1);
    const to = endOfMonth(new Date(selectedYear, toMonth, 1));
    try {
      const response = await ApiService.axiosApi(
        `/wuareport/getwuareportbymonth`,
        {
          method: "GET",
          params: {
            pageNumber: 0,
            pageSize: 0,
            fromDate: format(from, "yyyy-MM-dd HH:mm:ss"),
            toDate: format(to, "yyyy-MM-dd HH:mm:ss"),
            Id: selectedStationId,
          },
        },
      );
      if (response.success) {
        transformData(response.data.data ?? []);
        if ((response.data.data ?? []).length === 0)
          toast.error("No data found");
      } else {
        toast.error("Unable to get data at this moment");
      }
    } catch (e) {
      console.log(e);
      toast.error("Unable to get data at this moment");
    }
  };

  // For export — fetch all data with pageNumber:0 pageSize:0
  const getAllDataForExport = async () => {
    const from = new Date(selectedYear, fromMonth, 1);
    const to = endOfMonth(new Date(selectedYear, toMonth, 1));
    try {
      const response = await ApiService.axiosApi(
        `/wuareport/getwuareportbymonth`,
        {
          method: "GET",
          params: {
            pageNumber: 0,
            pageSize: 0,
            fromDate: format(from, "yyyy-MM-dd HH:mm:ss"),
            toDate: format(to, "yyyy-MM-dd HH:mm:ss"),
            Id: selectedStationId,
          },
        },
      );
      if (response.success) {
        const raw = response.data.data ?? [];

        // Pivot into flat rows for Excel: { dateTime, HQ1: val, HQ2: val, ... }
        const hqSet = new Set<string>();
        const dateSet = new Set<string>();
        raw.forEach((r: any) => {
          hqSet.add(r.headQuaterName);
          dateSet.add(r.dateTime);
        });

        const hqs = Array.from(hqSet).sort();
        const dates = Array.from(dateSet).sort((a: string, b: string) => {
          const [ay, am] = a.split("-");
          const [by, bm] = b.split("-");
          return (
            new Date(+ay, MONTH_NAMES.indexOf(am), 1).getTime() -
            new Date(+by, MONTH_NAMES.indexOf(bm), 1).getTime()
          );
        });

        const exportRows = dates.map((dt) => {
          const rowObj: any = { "Date/Month": dt };
          hqs.forEach((hq) => {
            const found = raw.find(
              (r: any) => r.dateTime === dt && r.headQuaterName === hq
            );
            rowObj[hq] = found ? found.totalWaterDistrubuted ?? 0 : 0;
          });
          return rowObj;
        });
    
        // ── Footer row ──────────────────────────────
        const footerRow: any = { "Date/Month": "Total Water Distributed" };
        hqs.forEach((hq) => {
          const found = raw.find((r: any) => r.headQuaterName === hq);
          footerRow[hq] = found ? found.totalWaterDistributedRange ?? 0 : 0;
        });
        exportRows.push(footerRow); // ← yahan add karo, return se pehle
    
        return exportRows; // ← phir return
      }
    } catch (e) {
      console.log(e);
    }
  };

  // useEffect(() => {
  //   if (selectedStationId) fetchData(pageNumber);
  // }, [pageNumber]);

  const handleNext = useCallback(() => {
    setPageNumber((prev) => Math.min(totalPages, prev + 1));
  }, [totalPages]);

  const handlePrev = useCallback(() => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  }, []);

  const getValue = (dateTime: string, hq: string) => {
    const row = data.find(
      (r) => r.dateTime === dateTime && r.headQuaterName === hq,
    );
    return row ? (row.totalWaterDistrubuted ?? 0) : "-";
  };

  // Paginate dateList — show only current page rows
  const paginatedDates = dateList.slice(
    (pageNumber - 1) * pageSize,
    pageNumber * pageSize,
  );

  const exportFrom = format(
    new Date(selectedYear, fromMonth, 1),
    "yyyy-MM-dd HH:mm:ss",
  );
  const exportTo = format(
    endOfMonth(new Date(selectedYear, toMonth, 1)),
    "yyyy-MM-dd HH:mm:ss",
  );

  const getTotal = (hq: string) => {
    const rows = data.filter((r) => r.headQuaterName === hq);
    if (rows.length === 0) return 0;
    // totalWaterDistributedRange same hoga sabke liye ek HQ ka
    return rows[0].totalWaterDistributedRange ?? 0;
  };

  return (
    <>
      <Breadcrumbs
        mainTitle="WUA Wise Monthly Water Distribution"
        parent="WUA Report"
      />
      <div className="w-100 bg-white px-2 py-2">
        {/* Filter Bar */}
        <div
          className="bg-light-primary d-flex justify-content-around align-items-center flex-wrap"
          style={{
            width: "100%",
            minHeight: "100px",
            border: "1px solid rgb(43,94,94,0.1)",
            padding: "8px 0",
          }}
        >
          {/* Pumping Station */}
          {/* <div className="report-input-div mx-4 input-border d-flex flex-column">
            <Label>Pumping Station:</Label>
            <Dropdown isOpen={stationDropdownOpen} toggle={() => setStationDropdownOpen(!stationDropdownOpen)} className="custom-dropdown-wrapper">
              <DropdownToggle
                className="custom-dropdown-toggle d-flex align-items-center justify-content-between report-dropdown"
                style={{ fontWeight: 500, borderRadius: "5px", padding: "8px 16px", width: "200px", height: "36px" }}
                color="whitesmoke"
              >
                {selectedStationName ?? "Pumping Station"}
              </DropdownToggle>
              <DropdownMenu className="custom-dropdown-menu" style={{ overflowY: "auto", boxShadow: "0 0.5rem 1rem rgba(0,0,0,0.1)", borderRadius: "8px", padding: "8px 0" }}>
                {pumpingStationData.map((station: any) => (
                  <DropdownItem key={station.id} onClick={() => { setSelectedStationName(station.name); setSelectedStationId(station.id); }}>
                    {station.name}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div> */}

          {/* Year */}
          <div className="report-input-div mx-4 input-border d-flex flex-column">
            <Label>Year:</Label>
            <Dropdown
              isOpen={yearDropdownOpen}
              toggle={() => setYearDropdownOpen(!yearDropdownOpen)}
              className="custom-dropdown-wrapper"
            >
              <DropdownToggle
                className="custom-dropdown-toggle d-flex align-items-center justify-content-between report-dropdown"
                style={{
                  fontWeight: 500,
                  borderRadius: "5px",
                  padding: "8px 16px",
                  width: "140px",
                  height: "36px",
                }}
                color="whitesmoke"
              >
                {selectedYear}
              </DropdownToggle>
              <DropdownMenu
                className="custom-dropdown-menu"
                style={{
                  boxShadow: "0 0.5rem 1rem rgba(0,0,0,0.1)",
                  borderRadius: "8px",
                  padding: "8px 0",
                }}
              >
                {yearOptions.map((y) => (
                  <DropdownItem key={y} onClick={() => setSelectedYear(y)}>
                    {y}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>

          {/* From Month */}
          <div className="report-input-div mx-4 input-border d-flex flex-column">
            <Label>From Month:</Label>
            <Dropdown
              isOpen={fromMonthDropdownOpen}
              toggle={() => setFromMonthDropdownOpen(!fromMonthDropdownOpen)}
              className="custom-dropdown-wrapper"
            >
              <DropdownToggle
                className="custom-dropdown-toggle d-flex align-items-center justify-content-between report-dropdown"
                style={{
                  fontWeight: 500,
                  borderRadius: "5px",
                  padding: "8px 16px",
                  width: "140px",
                  height: "36px",
                }}
                color="whitesmoke"
              >
                {MONTH_NAMES[fromMonth]}
              </DropdownToggle>
              <DropdownMenu
                className="custom-dropdown-menu"
                style={{
                  boxShadow: "0 0.5rem 1rem rgba(0,0,0,0.1)",
                  borderRadius: "8px",
                  padding: "8px 0",
                }}
              >
                {MONTH_NAMES.map((name, idx) => (
                  <DropdownItem key={idx} onClick={() => setFromMonth(idx)}>
                    {name}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>

          {/* To Month */}
          <div className="report-input-div mx-4 input-border d-flex flex-column">
            <Label>To Month:</Label>
            <Dropdown
              isOpen={toMonthDropdownOpen}
              toggle={() => setToMonthDropdownOpen(!toMonthDropdownOpen)}
              className="custom-dropdown-wrapper"
            >
              <DropdownToggle
                className="custom-dropdown-toggle d-flex align-items-center justify-content-between report-dropdown"
                style={{
                  fontWeight: 500,
                  borderRadius: "5px",
                  padding: "8px 16px",
                  width: "140px",
                  height: "36px",
                }}
                color="whitesmoke"
              >
                {MONTH_NAMES[toMonth]}
              </DropdownToggle>
              <DropdownMenu
                className="custom-dropdown-menu"
                style={{
                  boxShadow: "0 0.5rem 1rem rgba(0,0,0,0.1)",
                  borderRadius: "8px",
                  padding: "8px 0",
                }}
              >
                {MONTH_NAMES.map((name, idx) => (
                  <DropdownItem key={idx} onClick={() => setToMonth(idx)}>
                    {name}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>

          {/* Generate */}
          <div className="d-flex flex-column" style={{ width: "8%" }}>
            <Label style={{ visibility: "hidden" }}>Generate</Label>
            <Button
              className="px-0"
              color="success"
              onClick={() => {
                if (!validateGenerate()) return;
                setPageNumber(1);
                fetchData(1);
              }}
              style={{
                width: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Generate
            </Button>
          </div>

          {/* Export */}
          <div style={{ width: "8%" }}>
            <Label style={{ visibility: "hidden" }}>Export</Label>
            <ExportExcel
              getData={getAllDataForExport}
              fileName="WUA Wise Monthly Water Distribution"
              EffectiveFrom={exportFrom}
              toDate={exportTo}
              hiddenColumns={[]}
              columnOrder={["Date/Month", ...headQuarters]}
            />
          </div>
        </div>

        {/* Table */}
        <div
          className="mt-2"
          style={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}
        >
          <table
            className="table table-sm table-hover text-center"
            style={{
              fontSize: "0.95rem",
              fontFamily: "Segoe UI, Roboto, sans-serif",
              width: "100%",
              minWidth: `${(headQuarters.length + 1) * 160}px`, // ensures all HQ cols visible
            }}
          >
            <thead className="bg-light-primary">
              <tr>
                <th
                  style={{
                    fontWeight: 600,
                    color: "#333",
                    padding: "10px 8px",
                    fontSize: "1rem",
                    verticalAlign: "middle",
                    whiteSpace: "nowrap",
                    minWidth: "120px",
                  }}
                >
                  Month
                </th>
                {headQuarters.map((hq) => (
                  <th
                    key={hq}
                    style={{
                      fontWeight: 600,
                      color: "#333",
                      padding: "10px 8px",
                      fontSize: "1rem",
                      whiteSpace: "nowrap",
                      minWidth: "160px",
                    }}
                  >
                    {hq}
                    <br />
                    <span style={{ fontWeight: 400, fontSize: "0.8rem", color: "#666" }}>(m³)</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="table-body">
              {paginatedDates.length > 0 ? (
                paginatedDates.map((dt, idx) => (
                  <tr key={idx}>
                    <td
                      style={{
                        padding: "6px 8px",
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {dt}
                    </td>
                    {headQuarters.map((hq) => (
                      <td key={hq} style={{ padding: "6px 8px" }}>
                        {getValue(dt, hq)}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={headQuarters.length + 1}
                    className="text-center text-muted py-3"
                  >
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
            {/* Footer */}
            {dateList.length > 0 && (
              <tfoot>
              <tr className="bg-light-primary">
                <td style={{ padding: "8px", textAlign: "center", whiteSpace: "nowrap", fontWeight: 600, color: "#333" }}>
                  Total Water Distributed(m³)
                </td>
                {headQuarters.map((hq) => (
                  <td key={hq} style={{ padding: "8px", fontWeight: 600, color: "#333" }}>
                    {getTotal(hq)}
                  </td>
                ))}
              </tr>
            </tfoot>
            )}
          </table>
        </div>

        {/* Pagination */}
        <div
          className="d-flex justify-content-end align-content-center"
          style={{ marginRight: "10px", marginTop: "10px" }}
        >
          <p style={{ marginRight: "10px", marginTop: "8px" }}>
            Page {pageNumber} of {totalPages || 1}
          </p>
          <button
            onClick={handlePrev}
            disabled={pageNumber === 1}
            className={`icon-button ${pageNumber === 1 ? "disabled" : ""}`}
          >
            <LeftIcon className="icon" />
          </button>
          <button
            onClick={handleNext}
            disabled={pageNumber === totalPages || totalPages === 0}
            className={`icon-button ${pageNumber === totalPages ? "disabled" : ""}`}
          >
            <RightIcon className="icon" />
          </button>
        </div>
      </div>
    </>
  );
};

export default HeadQuarterWaterReport;
